import ApiService from './api.service'
import type { PhaseStatusHistoryDto, PhaseStatusHistoryPageResponse, PhaseStatusHistoryQueryParams } from '@/types/phase'

/**
 * Service for managing project phase-related API endpoints
 */
class ProjectPhasesService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Phases Error'
      }
    })
  }

  /**
   * Fetch phases for a specific project
   */
  async getPhases(projectId: string): Promise<any> {
    return this.get(`/api/project/${projectId}/phases`)
  }

  /**
   * Get a specific phase by ID
   */
  async getPhaseById(projectId: string, phaseId: string): Promise<any> {
    return this.get(`/api/project/${projectId}/phases/${phaseId}`)
  }

  /**
   * Create a new phase
   */
  async createPhase(projectId: string, phaseData: any): Promise<any> {
    return this.post(`/api/project/${projectId}/phases`, phaseData)
  }

  /**
   * Update an existing phase
   */
  async updatePhase(projectId: string, phaseId: string, phaseData: any): Promise<any> {
    return this.put(`/api/project/${projectId}/phases/${phaseId}`, phaseData)
  }

  /**
   * Delete a phase
   */
  async deletePhase(projectId: string, phaseId: string): Promise<any> {
    return this.delete(`/api/project/${projectId}/phases/${phaseId}`)
  }

  /**
   * Mark phase as delivered
   */
  async markPhaseAsDelivered(projectId: string, phaseId: string, deliveredDate?: string): Promise<any> {
    const params = deliveredDate ? `?deliveredDate=${deliveredDate}` : ''
    return this.post(`/api/project/${projectId}/phases/${phaseId}/deliver${params}`, {})
  }

  /**
   * Get tasks for a specific phase
   */
  async getTasksByPhase(projectId: string, phaseId: string): Promise<any> {
    return this.get(`/api/project/${projectId}/phases/${phaseId}/tasks`)
  }

  /**
   * Get unassigned tasks (not linked to any phase)
   */
  async getUnassignedTasks(projectId: string): Promise<any> {
    return this.get(`/api/project/${projectId}/phases/unassigned/tasks`)
  }

  /**
   * Get all tasks for a project (regardless of phase assignment)
   */
  async getAllProjectTasks(projectId: string): Promise<any> {
    return this.get(`/api/project/${projectId}/tasks`)
  }

  /**
   * Configure phase with tasks (task-centric approach)
   * This sends a list of tasks with optional intervenant assignments
   */
  async configurePhaseWithTasks(projectId: string, phaseId: string, configData: any): Promise<any> {
    return this.post(`/api/project/${projectId}/phases/${phaseId}/configure-tasks`, configData)
  }

  /**
   * Get status timeline for a phase (chronological order)
   * Returns the complete status history for phase status visualization
   * @param projectId - The project ID
   * @param phaseId - The phase ID
   * @returns Promise that resolves with list of status history entries
   */
  async getPhaseStatusTimeline(projectId: string, phaseId: string): Promise<PhaseStatusHistoryDto[] | null> {
    return this.get<PhaseStatusHistoryDto[]>(`/api/project/${projectId}/phases/${phaseId}/status-timeline`)
  }

  /**
   * Get paginated status history for a phase
   * @param projectId - The project ID
   * @param phaseId - The phase ID
   * @param params - Query parameters for pagination and sorting
   * @returns Promise that resolves with paginated status history
   */
  async getPhaseStatusHistory(
    projectId: string,
    phaseId: string,
    params?: PhaseStatusHistoryQueryParams
  ): Promise<PhaseStatusHistoryPageResponse | null> {
    const queryParams = new URLSearchParams()
    
    if (params?.page !== undefined) {
      queryParams.append('page', params.page.toString())
    }
    if (params?.size !== undefined) {
      queryParams.append('size', params.size.toString())
    }
    if (params?.sort) {
      queryParams.append('sort', params.sort)
    }
    if (params?.direction) {
      queryParams.append('direction', params.direction)
    }
    
    const queryString = queryParams.toString()
    const url = `/api/project/${projectId}/phases/${phaseId}/status-history${queryString ? '?' + queryString : ''}`
    
    return this.get<PhaseStatusHistoryPageResponse>(url)
  }

  /**
   * Change phase status using state machine
   * @param projectId - The project ID
   * @param phaseId - The phase ID
   * @param newStatus - The new status to transition to
   * @param comment - Optional comment about the status change
   * @returns Promise that resolves with updated phase
   */
  async changePhaseStatus(
    projectId: string,
    phaseId: string,
    newStatus: string,
    comment?: string
  ): Promise<any> {
    return this.post(`/api/project/${projectId}/phases/${phaseId}/change-status`, {
      newStatus,
      comment
    })
  }
}

export default new ProjectPhasesService()
